import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Grid,
  TextField,
  Typography,
  Switch,
  FormControlLabel,
  Button,
  CircularProgress,
} from '@mui/material';
import { Add, Delete, Image as ImageIcon, Save, DragIndicator } from '@mui/icons-material';
import { toast } from 'react-hot-toast';
import { apiService } from '../../services/api';

type Banner = {
  imageUrl: string;
  link?: string;
  order?: number;
};

type OfferConfig = {
  floatingText: Record<string, string>;
  offerPercentageText: Record<string, string>;
  isActive: boolean;
  banners: Banner[];
};

const emptyBanner: Banner = { imageUrl: '', link: '', order: 0 };

const OffersConfig: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [config, setConfig] = useState<OfferConfig>({
    floatingText: { EN: 'Special Offer' },
    offerPercentageText: { EN: '50% OFF' },
    isActive: true,
    banners: [],
  });
  const [uploadingBanner, setUploadingBanner] = useState<number | null>(null);
  const [draggingBanner, setDraggingBanner] = useState<number | null>(null);
  const [bulkUploading, setBulkUploading] = useState(false);

  useEffect(() => {
    loadConfig();
  }, []);

  const sortedBanners = useMemo(
    () => [...config.banners].sort((a, b) => (a.order ?? 0) - (b.order ?? 0)),
    [config.banners],
  );
  const loadConfig = async () => {
    setLoading(true);
    try {
      const res = await apiService.getOffersConfig();
      if (res.success && res.data) {
        setConfig({
          floatingText: res.data.floatingText ?? { EN: 'Special Offer' },
          offerPercentageText: res.data.offerPercentageText ?? { EN: '50% OFF' },
          isActive: res.data.isActive ?? true,
          banners: res.data.banners ?? [],
        });
      }
    } catch (e: any) {
      toast.error(e?.message || 'Failed to load offers config');
    } finally {
      setLoading(false);
    }
  };

  const handleBannerFile = async (idx: number, file: File) => {
    setUploadingBanner(idx);
    try {
      const resp = await apiService.uploadOfferImage(file);
      if (resp.success && resp.data?.url) {
        updateBanner(idx, 'imageUrl', resp.data.url);
        toast.success('Banner uploaded');
      } else {
        toast.error(resp.error ?? 'Upload failed');
      }
    } catch (e: any) {
      toast.error(e?.message ?? 'Upload failed');
    } finally {
      setUploadingBanner(null);
    }
  };

  const handleBulkFiles = async (fileList: FileList) => {
    if (!fileList || fileList.length === 0) return;
    setBulkUploading(true);
    const files = Array.from(fileList);
    const maxOrder = config.banners.reduce((m, b) => Math.max(m, b.order ?? 0), -1);
    let added = 0;
    for (const file of files) {
      try {
        const resp = await apiService.uploadOfferImage(file);
        if (resp.success && resp.data?.url) {
          const nextOrder = maxOrder + 1 + added;
          added++;
          setConfig((prev) => ({
            ...prev,
            banners: [
              ...prev.banners,
              { ...emptyBanner, imageUrl: resp.data!.url, order: nextOrder },
            ],
          }));
        } else {
          toast.error(resp.error ?? `Upload failed for ${file.name}`);
        }
      } catch (e: any) {
        toast.error(e?.message ?? `Upload failed for ${file.name}`);
      }
    }
    // Save after bulk upload finishes
    const nextConfig = {
      ...config,
      banners: config.banners,
    };
    saveConfigWithData(nextConfig, true);
    setBulkUploading(false);
  };

  const addBanner = () => {
    const maxOrder = config.banners.reduce((m, b) => Math.max(m, b.order ?? 0), 0);
    const next = {
      ...config,
      banners: [...config.banners, { ...emptyBanner, order: maxOrder + 1 }],
    };
    setConfig(next);
    saveConfigWithData(next, true);
  };

  const removeBanner = (idx: number) => {
    const next = { ...config, banners: config.banners.filter((_, i) => i !== idx) };
    setConfig(next);
    saveConfigWithData(next, true);
  };

  const moveBanner = (sortedIdx: number, direction: -1 | 1) => {
    const arr = [...sortedBanners];
    const target = sortedIdx + direction;
    if (target < 0 || target >= arr.length) return;
    const temp = arr[sortedIdx];
    arr[sortedIdx] = arr[target];
    arr[target] = temp;
    const next = { ...config, banners: arr.map((b, i) => ({ ...b, order: i })) };
    setConfig(next);
    saveConfigWithData(next, true);
  };

  const saveConfigWithData = async (data: OfferConfig, silent = false) => {
    const payload = data;
    setLoading(true);
    try {
      const res = await apiService.updateOffersConfig(payload);
      if (res.success) {
        if (!silent) toast.success('Offers saved');
      } else if (!silent) {
        toast.error(res.error ?? 'Failed to save');
      }
    } catch (e: any) {
      if (!silent) toast.error(e?.message ?? 'Failed to save');
    } finally {
      setLoading(false);
    }
  };

  const saveConfig = async () => {
    await saveConfigWithData(config, false);
  };

  const updateBanner = (idx: number, field: keyof Banner, value: any) => {
    const next = [...config.banners];
    next[idx] = { ...next[idx], [field]: value };
    setConfig({ ...config, banners: next });
  };

  return (
    <Box p={3}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h5" fontWeight={700}>
          Offers Configuration
        </Typography>
        <Box display="flex" alignItems="center" gap={2}>
          <FormControlLabel
            control={
              <Switch
                checked={config.isActive}
                onChange={(e) => setConfig({ ...config, isActive: e.target.checked })}
              />
            }
            label="Active"
          />
          <Button
            variant="contained"
            startIcon={<Save />}
            onClick={saveConfig}
            disabled={loading}
          >
            {loading ? 'Saving...' : 'Save'}
          </Button>
        </Box>
      </Box>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="subtitle1" fontWeight={600} gutterBottom>
            Floating Offer Pill
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Floating Text (EN)"
                value={config.floatingText?.EN ?? ''}
                onChange={(e) =>
                  setConfig({
                    ...config,
                    floatingText: { ...config.floatingText, EN: e.target.value },
                  })
                }
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Floating Text (HI)"
                value={config.floatingText?.HI ?? ''}
                onChange={(e) =>
                  setConfig({
                    ...config,
                    floatingText: { ...config.floatingText, HI: e.target.value },
                  })
                }
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Offer Percentage (EN)"
                value={config.offerPercentageText?.EN ?? ''}
                onChange={(e) =>
                    setConfig({
                      ...config,
                      offerPercentageText: { ...config.offerPercentageText, EN: e.target.value },
                    })
                  }
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Offer Percentage (HI)"
                value={config.offerPercentageText?.HI ?? ''}
                onChange={(e) =>
                    setConfig({
                      ...config,
                      offerPercentageText: { ...config.offerPercentageText, HI: e.target.value },
                    })
                  }
              />
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      <Card sx={{ mb: 3 }}>
        <CardHeader
          title="Banners (slider)"
          action={
            <Button size="small" startIcon={<Add />} onClick={addBanner}>
              Add Banner
            </Button>
          }
        />
        <CardContent>
          <Box
            sx={{
              border: '1px dashed #666',
              borderRadius: 2,
              p: 3,
              textAlign: 'center',
              backgroundColor: 'rgba(255,255,255,0.02)',
              mb: 2,
            }}
            onDragOver={(e) => e.preventDefault()}
            onDrop={(e) => {
              e.preventDefault();
              handleBulkFiles(e.dataTransfer.files);
            }}
          >
            <Typography variant="subtitle1" fontWeight={600} gutterBottom>
              Upload Banners
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Drag & drop images here or click to select. Multiple files supported.
            </Typography>
            <Button
              sx={{ mt: 2 }}
              variant="outlined"
              startIcon={
                bulkUploading ? <CircularProgress size={16} color="inherit" /> : <ImageIcon />
              }
              component="label"
              disabled={bulkUploading}
            >
              {bulkUploading ? 'Uploading...' : 'Choose Files'}
              <input
                hidden
                type="file"
                multiple
                accept="image/*,video/*"
                onChange={(e) => {
                  const files = e.target.files;
                  if (files) handleBulkFiles(files);
                }}
              />
            </Button>
          </Box>
          {sortedBanners.length === 0 && (
            <Typography color="text.secondary">No banners yet.</Typography>
          )}
          {sortedBanners.length > 0 && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Total: {sortedBanners.length}
            </Typography>
          )}
          <Grid container spacing={2}>
            {sortedBanners.map((banner, idx) => (
              <Grid item xs={12} md={6} key={`${banner.imageUrl}-${idx}`}>
                <Card variant="outlined">
                  <CardContent>
                    <Grid container spacing={2} alignItems="center">
                      <Grid item xs={12} md={4} display="flex" alignItems="center" gap={1}>
                        <TextField
                          label="Order"
                          type="number"
                          value={banner.order ?? 0}
                          onChange={(e) => updateBanner(idx, 'order', Number(e.target.value))}
                          onBlur={() => saveConfigWithData(config, true)}
                          sx={{ width: 120 }}
                        />
                        <Button
                          size="small"
                          variant="outlined"
                          component="label"
                          disabled={uploadingBanner === idx}
                          startIcon={
                            uploadingBanner === idx ? (
                              <CircularProgress size={16} color="inherit" />
                            ) : (
                              <ImageIcon fontSize="small" />
                            )
                          }
                        >
                          {uploadingBanner === idx ? 'Uploading' : 'Upload'}
                          <input
                            hidden
                            accept="image/*,video/*"
                            type="file"
                            onChange={(e) => {
                              const file = e.target.files?.[0];
                              if (file) handleBannerFile(idx, file);
                            }}
                          />
                        </Button>
                      </Grid>
                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          label="Link (optional)"
                          value={banner.link ?? ''}
                          onChange={(e) => updateBanner(idx, 'link', e.target.value)}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <Box
                          onDragOver={(e) => {
                            e.preventDefault();
                            setDraggingBanner(idx);
                          }}
                          onDragLeave={() => setDraggingBanner(null)}
                          onDrop={(e) => {
                            e.preventDefault();
                            setDraggingBanner(null);
                            const file = e.dataTransfer.files?.[0];
                            if (file) handleBannerFile(idx, file);
                          }}
                          sx={{
                            border: '1px dashed',
                            borderColor: draggingBanner === idx ? 'primary.main' : '#444',
                            borderRadius: 1,
                            p: 2,
                            textAlign: 'center',
                            backgroundColor: draggingBanner === idx ? 'rgba(25,118,210,0.08)' : 'transparent',
                            cursor: 'pointer',
                          }}
                        >
                          {banner.imageUrl ? (
                            <Box
                              sx={{
                                borderRadius: 1,
                                overflow: 'hidden',
                                border: '1px solid #333',
                              }}
                            >
                              {banner.imageUrl.toLowerCase().endsWith('.mp4') ||
                              banner.imageUrl.toLowerCase().endsWith('.mov') ||
                              banner.imageUrl.toLowerCase().endsWith('.webm') ? (
                                <video
                                  src={banner.imageUrl}
                                  style={{ width: '100%', height: 160, objectFit: 'cover' }}
                                  muted
                                  loop
                                  controls
                                />
                              ) : (
                                <img
                                  src={banner.imageUrl}
                                  alt="banner preview"
                                  style={{ width: '100%', height: 160, objectFit: 'cover' }}
                                />
                              )}
                            </Box>
                          ) : (
                            <Box display="flex" flexDirection="column" alignItems="center" gap={1} py={2}>
                              <ImageIcon color="action" />
                              <Typography variant="body2" color="text.secondary">
                                Drag & drop banner image/video here, or click Upload
                              </Typography>
                            </Box>
                          )}
                        </Box>
                      </Grid>
                      <Grid item xs={12} display="flex" justifyContent="space-between" alignItems="center">
                        <Button
                          size="small"
                          startIcon={<DragIndicator />}
                          onClick={() => moveBanner(idx, -1)}
                          disabled={idx === 0}
                        >
                          Up
                        </Button>
                        <Button
                          size="small"
                          startIcon={<DragIndicator />}
                          onClick={() => moveBanner(idx, 1)}
                          disabled={idx === sortedBanners.length - 1}
                        >
                          Down
                        </Button>
                        <Button
                          size="small"
                          color="error"
                          startIcon={<Delete />}
                          onClick={() => removeBanner(idx)}
                        >
                          Remove
                        </Button>
                      </Grid>
                      <Grid item xs={12} display="flex" justifyContent="flex-end">
                        <Button
                          size="small"
                          color="error"
                          startIcon={<Delete />}
                          onClick={() => {
                            const next = config.banners.filter((_, i) => i !== idx);
                            setConfig({ ...config, banners: next });
                          }}
                        >
                          Remove
                        </Button>
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </CardContent>
      </Card>
    </Box>
  );
};

export default OffersConfig;

